import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { withDefaultPxUnits } from "./styleLength";

interface StyleTextInputProps {
  /** Knob label. */
  label: string;
  /** Current authored value ("" when unset). */
  value: string;
  /** Placeholder hinting the expected CSS syntax. */
  placeholder?: string;
  /** Keeps bare numbers bare, for knobs writing a unitless attribute. */
  unitless?: boolean;
  /** Drops the visible label, leaving it on the input for assistive tech. */
  hideLabel?: boolean;
  /** Extra classes for the input, e.g. a fixed width in a toolbar row. */
  inputClassName?: string;
  /** Blocks editing (nothing selected, or the document is locked). */
  disabled?: boolean;
  /** Commits the new value ("" clears the property). */
  onCommit: (value: string) => void;
}

/**
 * Free-text style knob: shows the authored inline value and commits on blur or
 * Enter, defaulting bare numbers to px. An emptied input clears the property.
 * The parent should key this component on the selected node + value so it
 * resets when the selection or source changes.
 * @param label - Knob label
 * @param value - Current authored value
 * @param placeholder - Placeholder hinting the expected CSS syntax
 * @param unitless - Keeps bare numbers bare
 * @param hideLabel - Drops the visible label for compact rows
 * @param inputClassName - Extra classes for the input
 * @param disabled - Blocks editing
 * @param onCommit - Commits the new value
 */
export function StyleTextInput({
  label,
  value,
  placeholder,
  unitless,
  hideLabel,
  inputClassName,
  disabled,
  onCommit,
}: StyleTextInputProps) {
  const input = (
    <Input
      className={cn("h-8 text-xs", inputClassName)}
      aria-label={label}
      title={label}
      disabled={disabled}
      defaultValue={value}
      placeholder={placeholder}
      onBlur={(event) => {
        const next = unitless
          ? event.target.value
          : withDefaultPxUnits(event.target.value);
        event.target.value = next;
        if (next !== value) {
          onCommit(next);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.currentTarget.blur();
        }
      }}
    />
  );
  if (hideLabel) {
    return input;
  }
  return (
    <div>
      <Label className="mb-1 block text-[11px]">{label}</Label>
      {input}
    </div>
  );
}
